import React, { useState, useEffect, useCallback } from "react";
import Header from "../components/Header.tsx";
import NewClientsChart from "../components/NewClientsChart.tsx";
import SettingsPopUp from "../components/SettingsPopUp.tsx";
import { chartData, filterDataByDateRange } from "../data/chart-data";
import { getKPIDataForMonth } from "../data/kpi-data";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import dayjs from "dayjs";
import "dayjs/locale/en";
import DateRangeSelector from "../components/DateRangeSelector.tsx";
import apiService from "../services/api";
import Timer from "../img/timer.svg";
import UserChart from "../img/user-chart.svg";

const theme = createTheme();

const Dashboard = ({
  user,
  setProfileSettingsIsOpen,
  profileSettingsIsOpen,
}) => {
  const [activeMetricIndex, setActiveMetricIndex] = useState(0);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filteredChartData, setFilteredChartData] = useState(chartData);
  const [currentKPIData, setCurrentKPIData] = useState(getKPIDataForMonth(dayjs()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatKPIData = useCallback((metrics) => {
    return [
      {
        image: (
          <i className="fa-solid fa-users text-darkBlue absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"></i>
        ),
        description: "New Clients",
        value: metrics.newClients.value,
      },
      {
        image: (
          <img
            src={Timer}
            alt="timer"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        ),
        description: "Average Response Time",
        value: metrics.averageResponseTime.value,
      },
      {
        image: (
          <i className="fa-solid fa-trophy text-darkBlue absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"></i>
        ),
        description: "Average Lead Quality Score",
        value: metrics.averageLeadQualityScore.value,
      },
      {
        image: (
          <img
            src={UserChart}
            alt="user-chart"
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        ),
        description: "Converted Clients",
        value: metrics.convertedClients.value,
      },
      {
        image: (
          <i className="fa-solid fa-chart-simple text-darkBlue absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"></i>
        ),
        description: "Conversion Rate",
        value: metrics.conversionRate.value,
      },
    ];
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getDashboardOverview();
      if (response.success) {
        const { currentMonth, chartData: newChartData } = response.data;
        
        // Update KPI data with real metrics
        const formattedKPIData = formatKPIData(currentMonth.metrics);
        setCurrentKPIData(formattedKPIData);
        
        // Update chart data
        setFilteredChartData(newChartData || []);
        
        console.log('✅ Dashboard data loaded successfully', response.data);
      } else {
        throw new Error(response.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('❌ Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
      // Keep using static data as fallback
      setCurrentKPIData(getKPIDataForMonth(dayjs()));
      setFilteredChartData(chartData);
    } finally {
      setLoading(false);
    }
  }, [formatKPIData]);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const response = await apiService.getDashboardStats();
      if (response.success) {
        console.log('✅ Dashboard stats loaded successfully', response.data);
        // Stats data can be used here if needed in the future
      } else {
        console.warn('⚠️ Failed to load dashboard stats:', response.message);
      }
    } catch (err) {
      console.warn('⚠️ Error loading dashboard stats (non-critical):', err.message || err);
      // Stats are optional, don't show error to user
    }
  }, []);

  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchData = async () => {
      await fetchDashboardData();
      await fetchDashboardStats();
    };
    fetchData();
  }, [fetchDashboardData, fetchDashboardStats]);

  

  const handleDateChange = async (newValue) => {
    if (newValue) {
      setSelectedDate(newValue);
      
      try {
        const year = newValue.year();
        const month = newValue.month() + 1; // dayjs months are 0-based
        
        const response = await apiService.getKPIMetrics(year, month);
        if (response.success) {
          const formattedData = formatKPIData(response.data.metrics);
          setCurrentKPIData(formattedData);
          console.log(`✅ KPI metrics loaded for ${year}-${month}`, response.data);
        } else {
          // Fallback to static data
          setCurrentKPIData(getKPIDataForMonth(newValue));
        }
      } catch (err) {
        console.error('Error loading KPI data for selected month:', err);
        // Fallback to static data
        setCurrentKPIData(getKPIDataForMonth(newValue));
      }
    }
  };

  const handleStartDateChange = (newValue) => {
    setStartDate(newValue);
    updateFilteredData(newValue, endDate);
  };

  const handleEndDateChange = (newValue) => {
    setEndDate(newValue);
    updateFilteredData(startDate, newValue);
  };

  const updateFilteredData = async (start, end) => {
    try {
      if (start && end) {
        const response = await apiService.getChartData(
          start.format('YYYY-MM-DD'),
          end.format('YYYY-MM-DD')
        );
        
        if (response.success && response.data.chartData) {
          setFilteredChartData(response.data.chartData);
          console.log(`✅ Chart data loaded for date range: ${start.format('YYYY-MM-DD')} to ${end.format('YYYY-MM-DD')}`, response.data);
        } else {
          // Fallback to static data filtering
          const filteredData = filterDataByDateRange(
            chartData,
            start.format('YYYY-MM-DD'),
            end.format('YYYY-MM-DD')
          );
          setFilteredChartData(filteredData.length > 0 ? filteredData : chartData);
          console.warn('⚠️ Using fallback chart data for date range');
        }
      } else {
        // Load default chart data (last 12 months)
        const response = await apiService.getChartData();
        if (response.success && response.data.chartData) {
          setFilteredChartData(response.data.chartData);
          console.log('✅ Default chart data loaded (last 12 months)', response.data);
        } else {
          setFilteredChartData(chartData);
          console.warn('⚠️ Using fallback static chart data');
        }
      }
    } catch (err) {
      console.error('Error loading chart data:', err);
      // Fallback to static data
      if (start && end) {
        const filteredData = filterDataByDateRange(
          chartData,
          start.format('YYYY-MM-DD'),
          end.format('YYYY-MM-DD')
        );
        setFilteredChartData(filteredData.length > 0 ? filteredData : chartData);
      } else {
        setFilteredChartData(chartData);
      }
    }
  };

  const handleClearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
    updateFilteredData(null, null); // This will load default data from API
  };

  const slotProps = {
    desktopPaper: {
      sx: {
        mt: 1.25,
        transform: "translateX(calc(0% - 30px))",
        borderRadius: "12px",
        border: "1px solid #323C48",
        boxShadow: "box-shadow: 0 4px 24px #CBD5E066",
        "& .Mui-selected": {
          backgroundColor: "#407097 !important",
          color: "#FFFFFF !important",
        },
      },
    },
    textField: {
      placeholder: "Date Range",
      sx: {
        width: "fit-content",
        "& .MuiInputLabel-root": {
          display: "none",
        },
        "& .MuiOutlinedInput-root": {
          fontFamily: "Inter, sans-serif",
          fontSize: "14px",
          fontWeight: "600",
          color: "#222830",
          height: "40px",
          borderRadius: "24px",
          backgroundColor: "#F3F5F7",
          display: "flex",
          alignItems: "center",
          "&:hover": {
            borderColor: "#407097",
          },
          "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#407097",
              borderWidth: "2px",
            },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#C3CBD5",
          },
        },
        "& .MuiInputBase-input": {
          padding: "0 12px",
          "&::placeholder": {
            color: "#323C48",
            opacity: 1,
          },
        },
        "& .MuiSvgIcon-root": {
          color: "#323C48",
        },
      },
    },
  };

  return (
    <>
      <Header setProfileSettingsIsOpen={setProfileSettingsIsOpen} />
      <div className="flex flex-col px-10 py-3 h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {/* Loading Indicator */}
          {loading && (
            <div className="flex items-center justify-center h-64">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-darkBlue"></div>
                <span className="text-h3 text-darkGray">Loading dashboard data...</span>
              </div>
            </div>
          )}
          
          {/* Error Message */}
          {error && !loading && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <i className="fa-solid fa-exclamation-triangle text-red-500 mr-2"></i>
                <span className="text-red-700">
                  Failed to load dashboard data: {error}. Using fallback data.
                </span>
              </div>
            </div>
          )}
          
          {/* Dashboard Content */}
          {!loading && (
            <>
              <div className="border border-lighterGray rounded-3xl mh-box-shadow mb-6">
                <div className="flex flex-col md:flex-row justify-between gap-2 p-6 items-center">
                  <p className="text-h2 font-semibold text-darkestSlate">
                    KPI by Month
                  </p>
                  <ThemeProvider theme={theme}>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale="en"
                    >
                      <DatePicker
                        views={["year", "month"]}
                        value={selectedDate}
                        onChange={handleDateChange}
                        slotProps={slotProps}
                      />
                    </LocalizationProvider>
                  </ThemeProvider>
                </div>
                <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 p-6">
                  {currentKPIData.map((metric, index) => (
                    <div
                      key={index}
                      onClick={() => setActiveMetricIndex(index)}
                      className={`h-[175px] flex flex-col justify-center items-center gap-y-3 border border-lighterGray rounded-3xl p-4 cursor-pointer transition-colors ${
                        activeMetricIndex === index ? "bg-[#DBE7F0]" : ""
                      }`}
                    >
                      <div className="relative">
                        <div className="w-[46px] h-[46px] bg-lightestBlue rounded-xl rotate-45"></div>
                        {metric.image}
                      </div>
                      <p className="text-h3 font-medium text-darkGray text-center">
                        {metric.description}
                      </p>
                      <span className="text-h1 font-semibold text-pureBlack">
                        {metric.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="border border-lighterGray rounded-3xl mh-box-shadow">
                <div className="flex flex-col md:flex-row justify-between gap-2 p-6 border-b border-lighterGray items-center">
                  <p className="text-h2 font-semibold text-darkestSlate text-center">
                    New Clients Detail
                  </p>
                  <DateRangeSelector
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={handleStartDateChange}
                    onEndDateChange={handleEndDateChange}
                    onClear={handleClearDateRange}
                  />
                </div>
                <div className="overflow-x-auto">
                  <NewClientsChart data={filteredChartData} dateRange={[startDate, endDate]} />
                </div>
              </div>
            </>
          )}
          {/* End Dashboard Content */}
        </div>
      </div>
      {profileSettingsIsOpen && (
        <SettingsPopUp
          setProfileSettingsIsOpen={setProfileSettingsIsOpen}
          user={user}
        />
      )}
    </>
  );
};

export default Dashboard;
